'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import type { ApiTokenLimitValues } from "../types";

/** Mint one personal API token. The plaintext comes back once; the register is refetched so the new row
 *  (and the counters it will start collecting) is the daemon's own, not the client's guess at it. */
export function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; expiresAt?: string }) => elowenClient.createApiToken(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.apiTokens }); },
  });
}

/** Replace one token's limits; the register carries them, so it is refetched. */
export function useSetApiTokenLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; limits: ApiTokenLimitValues }) => elowenClient.setApiTokenLimits(v.id, v.limits),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.apiTokens }); },
  });
}

/** Issue a new secret for one token: the row keeps its id, limits, counters and history, so only the
 *  register (whose prefix column just changed) needs refetching. The plaintext comes back once. */
export function useRegenerateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => elowenClient.regenerateApiToken(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: QUERY_KEYS.apiTokens }); },
  });
}

/** Delete one token. The register is refetched FIRST, so the panel moves off the deleted token; only then
 *  are that token's cached statistics dropped. Invalidating them instead would refetch them for the panel
 *  still showing the deleted token, and the daemon answers that with a 404. Other tokens' counters are
 *  untouched by a delete, so their statistics stay as they are. */
export function useDeleteApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => elowenClient.deleteApiToken(id),
    onSuccess: async (_result, id) => {
      await qc.invalidateQueries({ queryKey: QUERY_KEYS.apiTokens });
      qc.removeQueries({ queryKey: [...QUERY_KEYS.apiTokenStats, id] });
    },
  });
}
